import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Eye, Lock, Minus, Shield } from 'lucide-react';
import { api, type Role } from '../lib/api';
import { useAuth } from '../contexts/use-auth';
import type { AppRole } from '../contexts/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import {
  PERMISSIONS,
  ROLE_DESCRIPTIONS,
  ROLE_ORDER,
  permissionsByGroup,
  type Permission,
  type PermissionDef,
} from '../lib/permissions';
import { cn } from '../lib/utils';

const ROLE_ICONS: Record<AppRole, typeof Shield> = { Admin: Shield, SafetyNet: Eye };

/**
 * What each role can do, as a matrix.
 *
 * Read-only on purpose: the two roles are defined in code (`ROLES` in
 * api/src/permissions.ts), not in a table, so there is nothing here to edit.
 * Per-person exceptions are granted on the Admin Users page, where the same
 * permissions appear as deltas against whichever role someone holds.
 */
export function Roles() {
  const { role: myRole } = useAuth();
  const [selected, setSelected] = useState<Permission | null>(null);

  // The server's view of each role, not the SPA's copy of the catalog — a matrix
  // built entirely client-side could not show a drift if there were one.
  const list = useQuery({ queryKey: ['roles'], queryFn: () => api.listRoles(), staleTime: 60_000 });
  const roles = list.data?.items ?? [];
  const groups = permissionsByGroup();
  const detail = selected ? groups.flatMap((g) => g.permissions).find((p) => p.key === selected) : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Roles &amp; Permissions</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          What each role can do. Every permission below is checked by the API — hiding a button is not
          what stops the action.
        </p>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2">
        {ROLE_ORDER.map((roleId) => {
          const RoleIcon = ROLE_ICONS[roleId];
          const server = roles.find((r) => r.roleId === roleId);
          return (
            <div
              key={roleId}
              className={cn(
                'rounded-lg border bg-panel px-3.5 py-3',
                roleId === myRole ? 'border-accent-line' : 'border-line',
              )}
            >
              <div className="flex items-center gap-2">
                <Badge variant={roleId === 'Admin' ? 'admin' : 'safetynet'}>
                  <RoleIcon className="h-3 w-3" />
                  {roleId}
                </Badge>
                {roleId === myRole ? (
                  <span className="text-[11px] tracking-[0.08em] text-accent uppercase">You</span>
                ) : null}
                <Badge variant="outline" className="ml-auto">
                  <Lock className="h-3 w-3" />
                  built-in
                </Badge>
              </div>
              <p className="mt-1.5 text-[13px] leading-[1.45] text-muted-foreground">
                {ROLE_DESCRIPTIONS[roleId]}
              </p>
              <div className="mt-2 text-[13px] tabular-nums text-muted-foreground">
                {server ? `${server.permissions.length} of ${PERMISSIONS.length} permissions` : '—'}
              </div>
            </div>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{list.isLoading ? 'Loading…' : 'Permission matrix'}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {list.error ? (
            <div className="text-sm text-destructive">
              Failed to load roles: {(list.error as Error).message}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="px-2 py-2 font-medium text-muted-foreground">Permission</th>
                  {ROLE_ORDER.map((roleId) => (
                    <th key={roleId} className="w-28 px-2 py-2 text-center font-semibold">
                      {roleId}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <GroupRows
                    key={group.group}
                    group={group.group}
                    permissions={group.permissions}
                    roles={roles}
                    selected={selected}
                    onSelect={(p) => setSelected((prev) => (prev === p ? null : p))}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {detail ? <EnforcementDetail def={detail} /> : null}
    </div>
  );
}

function GroupRows({
  group,
  permissions,
  roles,
  selected,
  onSelect,
}: {
  group: string;
  permissions: PermissionDef[];
  roles: Role[];
  selected: Permission | null;
  onSelect: (p: Permission) => void;
}) {
  return (
    <>
      <tr className="bg-panel-2">
        <td
          colSpan={ROLE_ORDER.length + 1}
          className="px-2 py-1.5 text-[12px] font-semibold tracking-[0.09em] text-muted-foreground uppercase"
        >
          {group}
        </td>
      </tr>
      {permissions.map((def) => (
        <tr
          key={def.key}
          onClick={() => onSelect(def.key)}
          className={cn(
            'cursor-pointer border-b border-line-soft transition-colors hover:bg-raise',
            selected === def.key && 'bg-accent-soft',
          )}
        >
          <td className="px-2 py-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs">{def.key}</span>
              {def.reserved ? (
                <Badge variant="outline" className="text-[10px]">
                  reserved
                </Badge>
              ) : null}
            </div>
            <div className="text-xs text-muted-foreground">{def.label}</div>
          </td>
          {ROLE_ORDER.map((roleId) => {
            const held = roles.find((r) => r.roleId === roleId)?.permissions.includes(def.key);
            return (
              <td key={roleId} className="px-2 py-2 text-center">
                {held ? (
                  <Check className="inline h-4 w-4 text-ok" aria-label="granted" />
                ) : (
                  <Minus className="inline h-3.5 w-3.5 text-muted-foreground/40" aria-label="not granted" />
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/**
 * The routes that actually check the selected permission. Publishing these is
 * what keeps the matrix honest: a permission that gates nothing has to say so
 * rather than sitting in the table looking enforced.
 */
function EnforcementDetail({ def }: { def: PermissionDef }) {
  return (
    <Card className="border-accent-line">
      <CardHeader>
        <CardTitle className="font-mono">{def.key}</CardTitle>
        <p className="text-sm text-muted-foreground">{def.description}</p>
      </CardHeader>
      <CardContent>
        <div className="pb-1.5 text-[12px] font-semibold tracking-[0.09em] text-muted-foreground uppercase">
          Enforced by
        </div>
        {def.enforcedBy.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {def.enforcedBy.map((route) => (
              <li key={route} className="font-mono text-xs text-fg-2">
                {route}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing yet — this permission is reserved for a feature that is not built. Granting it
            has no effect today.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
